import { AfterViewInit, ChangeDetectionStrategy, Component, NgZone, OnDestroy, OnInit } from '@angular/core';

import { BehaviorSubject, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { GridService, NotificationService } from '@core/services';
import { remToPx } from '@util/layout';

import { OrderInquiryShippingDetailResponseRow } from '#types/order';

import { AuthService } from '@auth/services';
import { CONSTANTS } from '@order/constants';
import { OrderService } from '@order/services';

@Component({
  templateUrl: './inquiry-shipping-detail-dialog.component.html',
  styleUrls: ['./inquiry-shipping-detail-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderInquiryShippingDetailDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  destroy$ = new Subject<void>();

  selectedRow: OrderInquiryShippingDetailResponseRow;

  openTrackingButtonDisabled$ = new BehaviorSubject<boolean>(true);
  copyTrackingButtonDisabled$ = new BehaviorSubject<boolean>(true);

  container: HTMLDivElement;
  containerParent: HTMLDivElement;
  containerResizeObserver: ResizeObserver;

  lookupId = 'order-inquiry-shipping-detail-dialog';

  gridId = CONSTANTS.GRID_ORDER_INQUIRY_SHIPPING_DETAIL;

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(
    public orderService: OrderService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private authService: AuthService,
    private ngZone: NgZone,
    public gridService: GridService,
    private translocoService: TranslocoService,
    private notificationService: NotificationService
  ) {}

  ngOnInit() {
    // close dialog if no longer authenticated
    this.authService.authenticated$
      .pipe(
        filter((authenticated) => !authenticated),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.close();
      });

    this.gridService
      .selectedRow$<OrderInquiryShippingDetailResponseRow>(this.gridId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedRow) => {
        if (selectedRow?.data) {
          this.openTrackingButtonDisabled$.next(false);
          this.copyTrackingButtonDisabled$.next(false);
        } else {
          this.openTrackingButtonDisabled$.next(true);
          this.copyTrackingButtonDisabled$.next(true);
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();

    this.destroyResizeObserver();
  }

  ngAfterViewInit() {
    this.initResizeObserver();
  }

  initResizeObserver() {
    this.containerResizeObserver = new ResizeObserver(() => {
      this.ngZone.run(() => {
        const header: HTMLDivElement = this.containerParent.querySelector('.p-dialog-header');
        const footer: HTMLDivElement = this.container.querySelector('.p-dialog-footer');
        const paginator: HTMLDivElement = this.container.querySelector('.p-paginator');

        let offset = 0;

        if (header) {
          offset += Math.floor(header.offsetHeight);
        }

        if (footer) {
          offset += Math.floor(footer.offsetHeight);
        }

        if (paginator) {
          offset += Math.floor(paginator.offsetHeight);
        }

        this.orderService.orderInquiryShippingDetailDialogDimensions$.next({
          container: {
            width: Math.floor(this.containerParent.offsetWidth),
            height: Math.floor(this.containerParent.offsetHeight),
          },
          grid: {
            width: Math.floor(this.containerParent.offsetWidth) - remToPx('2rem'),
            height: Math.floor(this.containerParent.offsetHeight) - offset,
          },
        });
      });
    });

    try {
      this.container = document.querySelector(`.${this.lookupId}`);

      if (this.container) {
        this.containerParent = this.container.closest(`.lookup-dialog`);

        if (this.containerParent) {
          this.containerResizeObserver.observe(this.containerParent);
        }
      }
    } catch {}
  }

  destroyResizeObserver() {
    try {
      if (this.containerResizeObserver && this.containerParent) {
        this.containerResizeObserver.unobserve(this.containerParent);
      }
    } catch {}
  }

  openTracking() {
    this.openTrackingButtonDisabled$.pipe(take(1)).subscribe((disabled) => {
      if (disabled) {
        return;
      }

      this.gridService
        .selectedRow$<OrderInquiryShippingDetailResponseRow>(this.gridId)
        .pipe(take(1))
        .subscribe((selectedRow) => {
          if (!selectedRow.data.trackingurl) {
            this.notificationService.error({
              message: this.translate('order.shipping_details_dialog.dialog.error.missing_tracking_url'),
            });

            return;
          } else if (!selectedRow.data.trackingno) {
            this.notificationService.error({
              message: this.translate('order.shipping_details_dialog.dialog.error.missing_tracking_number'),
            });

            return;
          }

          const trackingUrl = selectedRow.data.trackingurl.replace(/\{\}/gi, selectedRow.data.trackingno);

          window.open(trackingUrl, '_blank');
        });
    });
  }

  copyTracking() {
    this.copyTrackingButtonDisabled$.pipe(take(1)).subscribe((disabled) => {
      if (disabled) {
        return;
      }

      this.gridService
        .selectedRow$<OrderInquiryShippingDetailResponseRow>(this.gridId)
        .pipe(take(1))
        .subscribe((selectedRow) => {
          const trackingNumber = selectedRow.data.trackingno;

          navigator.clipboard
            .writeText(trackingNumber)
            .then(() => {
              this.notificationService.info({
                title: this.translate('order.shipping_details_dialog.grid.context_menu.copy_tracking_number.success.title'),
                message: this.translate('order.shipping_details_dialog.grid.context_menu.copy_tracking_number.success.message'),
                duration: 3000,
              });
            })
            .catch((err) => {
              this.notificationService.error({
                message: err,
              });
            });
        });
    });
  }

  close() {
    this.ref.close(undefined);
  }
}
